import { useState } from 'react'
import { Link } from 'react-router-dom'

import { NICHT_VORHANDEN, api, ohne404 } from '../api'
import { datum, zeitpunkt } from '../format'
import { useAktion, useLaden } from '../hooks'
import Meldung from './Meldung'
import type { PickupJob, PickupStatus } from '../typen'

/**
 * Der Abhol-Dienst bei der Othüna (22.09.2026): der zweite Weg, auf dem Daten hereinkommen.
 *
 * Die SelectLine-API ist nur im Intranet der Othüna erreichbar. Ein kleiner Dienst auf dem dortigen
 * Windows-Server fragt hier nach Aufträgen, führt sie lokal aus und schickt die Antworten zurück -
 * ausgehend, kein offener Port. Diese Karte zeigt, ob er steht, und erlaubt, einen Abruf von Hand
 * zu beauftragen („hol die Produktion seit dem Wochenende").
 *
 * Was daraus wird, ist ein Importvorschlag wie aus einem PDF: gebucht wird erst nach Freigabe.
 */

const ARTEN: { wert: string; titel: string; hinweis: string }[] = [
  { wert: 'production', titel: 'Produktion', hinweis: 'Tagesproduktion und Materialverbrauch für einen Zeitraum' },
  { wert: 'stock', titel: 'Bestände', hinweis: 'Bestand je Lager und Charge - Vergleichswert, wird nie gebucht' },
  { wert: 'goods_receipt', titel: 'Wareneingang', hinweis: 'Zugänge mit Liefertermin und Preis' },
  { wert: 'inventory', titel: 'Inventur', hinweis: 'Soll und Ist je Charge zur laufenden Inventur' },
  { wert: 'info', titel: 'Lebenszeichen', hinweis: 'nur Version und Erreichbarkeit der API' },
]

const STATUSTEXT: Record<string, { text: string; klasse: string }> = {
  open: { text: 'wartet auf den Dienst', klasse: '' },
  running: { text: 'in Arbeit', klasse: '' },
  done: { text: 'geliefert', klasse: 'gut' },
  failed: { text: 'fehlgeschlagen', klasse: 'gesperrt' },
}

/** Ein abgebrochener Abruf steht als erledigt da, mit `parameters.abgebrochen` und dem Vermerk in `error` (02.11.2026). */
function abgebrochen(a: PickupJob): boolean {
  return a.parameters?.abgebrochen === true
}

/** Die nächtliche Nachprüfung holt schon gebuchte Tage noch einmal; nur bei Abweichung entsteht ein Lauf (02.11.2026). */
function nachpruefung(a: PickupJob): boolean {
  return a.parameters?.nachpruefen === true
}

// Die Betriebsart (23.09.2026). Der Dienst lief da schon beim Kunden - aber gegen das SelectLine-
// Testsystem. Ohne diesen Schalter hätte die Tagesplanung dessen Zahlen als Vorschlag in die echte
// Kladde gelegt.
const BETRIEBSARTEN: { wert: PickupStatus['mode']; titel: string; hinweis: string }[] = [
  { wert: 'off', titel: 'Aus', hinweis: 'Der Dienst meldet sich, bekommt aber keine Aufträge.' },
  {
    wert: 'trial',
    titel: 'Probe',
    hinweis: 'Nur Lebenszeichen gehen hinaus, nichts wird geplant. Andere Aufträge warten.',
  },
  {
    wert: 'live',
    titel: 'Betrieb',
    hinweis: 'Täglich werden Produktion, Bestände und Wareneingänge geholt und als Vorschlag abgelegt.',
  },
]

function heute(): string {
  return new Date().toISOString().slice(0, 10)
}

export default function SelectLineDienst() {
  const [art, setArt] = useState('production')
  const [von, setVon] = useState('')
  const [bis, setBis] = useState(heute())
  const { fehler, erfolg, setErfolg, arbeitet, ausfuehren } = useAktion()

  const { daten: geholt, fehler: ladeFehler, neuLaden } = useLaden<[PickupStatus, PickupJob[]] | typeof NICHT_VORHANDEN>(
    () => ohne404(Promise.all([api.pickupStatus(), api.pickupJobs()])),
    [],
  )
  const fehlt = geholt === NICHT_VORHANDEN
  const [status, auftraege] = fehlt ? [null, []] : geholt ?? [null, []]

  function beauftragen() {
    const parameter: Record<string, string> = {}
    if (art === 'production' || art === 'goods_receipt') {
      if (von) parameter.von = von
      if (bis) parameter.bis = bis
    } else if (art === 'stock' || art === 'inventory') {
      if (bis) parameter.stichtag = bis
    }
    void ausfuehren(async () => {
      const neu = await api.createPickupJob(art, parameter)
      setErfolg(`Auftrag ${neu.id} steht bereit. Der Dienst holt ihn sich beim nächsten Durchgang.`)
      neuLaden()
    })
  }

  function umschalten(wert: PickupStatus['mode']) {
    if (
      wert === 'live' &&
      !window.confirm(
        'Auf Betrieb umschalten?\n\nAb dem nächsten Durchgang holt der Dienst täglich Zahlen aus SelectLine und legt ' +
          'sie als Vorschlag in die Kladde. Das ist nur richtig, wenn er gegen das produktive SelectLine zeigt - ' +
          'nicht gegen das Testsystem.',
      )
    ) {
      return
    }
    void ausfuehren(async () => {
      await api.setPickupMode(wert)
      setErfolg(`Betriebsart: ${BETRIEBSARTEN.find((b) => b.wert === wert)?.titel}.`)
      neuLaden()
    })
  }

  function verwerfen(auftrag: PickupJob) {
    if (!window.confirm(`Abruf ${auftrag.id} verwerfen? Er verschwindet aus der Liste der Fehler.`)) return
    void ausfuehren(async () => {
      await api.discardPickupJob(auftrag.id)
      neuLaden()
    })
  }

  /** Einen offenen oder laufenden Abruf stoppen - etwa einen mit falschem Zeitraum (27.10.2026: Auftrag 554). */
  function abbrechen(auftrag: PickupJob) {
    if (
      !window.confirm(
        `Abruf ${auftrag.id} abbrechen? Er geht nicht mehr hinaus; ist der Dienst schon dabei, wird seine Lieferung verworfen - ` +
          'es entsteht kein Importlauf. Fehlende Tage holt die nächtliche Planung von selbst.',
      )
    )
      return
    void ausfuehren(async () => {
      await api.abbrechenPickupJob(auftrag.id)
      setErfolg(`Abruf ${auftrag.id} abgebrochen. Er bleibt mit Vermerk in der Liste.`)
      neuLaden()
    })
  }

  function wiederholen(auftrag: PickupJob) {
    void ausfuehren(async () => {
      await api.retryPickupJob(auftrag.id)
      neuLaden()
    })
  }

  const zeitraum = art === 'production' || art === 'goods_receipt'
  const stichtag = art === 'stock' || art === 'inventory'

  return (
    <div className="karte">
      <div className="leiste">
        <b>Der Dienst auf dem Server der Othüna</b>
        {status && !status.configured && <span className="status gesperrt">hat sich noch nie gemeldet</span>}
        {status?.configured && (
          <span className="hinweis">
            zuletzt {zeitpunkt(status.last_contact)}
            {status.agent_version ? `, Dienst ${status.agent_version}` : ''}
            {status.api_version ? `, SelectLine ${status.api_version}` : ''}
          </span>
        )}
        <button style={{ marginLeft: 'auto' }} className="leise" onClick={() => neuLaden()}>
          Neu laden
        </button>
      </div>

      {ladeFehler && <Meldung art="fehler">Der Stand des Abhol-Dienstes ließ sich nicht laden: {ladeFehler}</Meldung>}
      {fehler && <Meldung art="fehler">{fehler}</Meldung>}
      {erfolg && <Meldung art="gut">{erfolg}</Meldung>}
      {fehlt && <p className="hinweis">Diese Auswertung liefert das Backend noch nicht.</p>}

      <p className="hinweis">
        Die SelectLine-API ist nur im Firmennetz der Othüna erreichbar. Ein kleiner Dienst auf dem dortigen Server
        fragt hier nach Aufträgen und schickt die Antworten zurück - es führt kein Weg von hier dorthin. Was ankommt,
        wird nie direkt gebucht: es steht als Vorschlag unter Importe.
      </p>

      {status && (
        <div className="leiste">
          <span className="hinweis">Betriebsart</span>
          {BETRIEBSARTEN.map((b) => (
            <button
              key={b.wert}
              className={status.mode === b.wert ? 'haupt' : 'leise'}
              disabled={arbeitet || status.mode === b.wert}
              onClick={() => umschalten(b.wert)}
            >
              {b.titel}
            </button>
          ))}
          <span className="hinweis">{BETRIEBSARTEN.find((b) => b.wert === status.mode)?.hinweis}</span>
        </div>
      )}
      {status && status.mode !== 'live' && (
        <Meldung art="warnung">
          Der Abruf läuft nicht im Betrieb: es werden keine Tagesaufträge angelegt, und ein von Hand beauftragter Abruf
          wartet{status.mode === 'trial' ? ', außer einem Lebenszeichen' : ''}, bis auf Betrieb umgeschaltet wird.
          Umschalten darf nur die Verwaltung.
        </Meldung>
      )}

      {status?.failed ? (
        <Meldung art="warnung">
          {status.failed} Auftrag/Aufträge sind fehlgeschlagen und bleiben stehen, bis jemand sie ansieht. Sie werden
          nicht stillschweigend wiederholt.
        </Meldung>
      ) : null}

      <div className="leiste">
        <label className="feld">
          <span className="hinweis">Was holen</span>
          <select value={art} onChange={(e) => setArt(e.target.value)}>
            {ARTEN.map((a) => (
              <option key={a.wert} value={a.wert}>
                {a.titel}
              </option>
            ))}
          </select>
        </label>
        {zeitraum && (
          <label className="feld">
            <span className="hinweis">von (leer = seit dem letzten gebuchten Tag)</span>
            <input type="date" value={von} onChange={(e) => setVon(e.target.value)} />
          </label>
        )}
        {(zeitraum || stichtag) && (
          <label className="feld">
            <span className="hinweis">{zeitraum ? 'bis' : 'Stichtag'}</span>
            <input type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
          </label>
        )}
        <button className="haupt" disabled={arbeitet} onClick={beauftragen}>
          {arbeitet ? 'Läuft…' : 'Abruf beauftragen'}
        </button>
      </div>
      <p className="hinweis">{ARTEN.find((a) => a.wert === art)?.hinweis}</p>

      <div className="tabellenrahmen">
        <table>
          <thead>
            <tr>
              <th>Angelegt</th>
              <th>Was</th>
              <th>Zeitraum</th>
              <th>Stand</th>
              <th>Ergebnis</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {auftraege.length === 0 && (
              <tr>
                <td colSpan={6} className="leer">
                  Noch kein Abruf.
                </td>
              </tr>
            )}
            {auftraege.map((a) => {
              const stand = abgebrochen(a)
                ? { text: 'abgebrochen', klasse: 'verworfen' }
                : (STATUSTEXT[a.status] ?? { text: a.status, klasse: '' })
              const p = a.parameters as { von?: string; bis?: string; stichtag?: string }
              return (
                <tr key={a.id}>
                  <td>{zeitpunkt(a.created_at)}</td>
                  <td>
                    {ARTEN.find((x) => x.wert === a.kind)?.titel ?? a.kind}
                    {nachpruefung(a) && (
                      <div className="hinweis" title="Holt schon gebuchte Tage noch einmal; nur wenn SelectLine inzwischen anders gebucht hat, entsteht ein korrigierter Tagesbericht.">
                        Nachprüfung
                      </div>
                    )}
                  </td>
                  <td>
                    {p.stichtag ? datum(p.stichtag)
                      : p.von || p.bis ? `${datum(p.von)} - ${datum(p.bis)}`
                      : <span className="hinweis">ohne</span>}
                  </td>
                  <td>
                    <span className={`status ${stand.klasse}`}>{stand.text}</span>
                    {a.error && <div className="hinweis">{a.error}</div>}
                  </td>
                  <td>
                    {a.import_run_id ? (
                      <Link className="hinweis-link" to={`/importe/${a.import_run_id}`}>
                        Lauf {a.import_run_id}
                      </Link>
                    ) : abgebrochen(a) ? (
                      ''
                    ) : a.status === 'done' ? (
                      <span className="hinweis">{nachpruefung(a) ? 'kein korrigierter Bericht' : 'nichts Neues'}</span>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {(a.status === 'open' || a.status === 'running') && (
                      <button className="leise" disabled={arbeitet} onClick={() => abbrechen(a)}>
                        Abbrechen
                      </button>
                    )}
                    {a.status === 'failed' && (
                      <>
                        <button className="leise" disabled={arbeitet} onClick={() => wiederholen(a)}>
                          Wiederholen
                        </button>{' '}
                        <button className="leise" disabled={arbeitet} onClick={() => verwerfen(a)}>
                          Verwerfen
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
